import { pushLayer } from "../utils/layers";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { HelpCircle } from "lucide-react";
import { apiFetch } from "../utils/api";
import { Markup, pageKeyFor, type HelpArticle } from "./helpMarkup";
import type { Tour, TourStep } from "./HelpTour";
const TourOverlay = lazy(() => import("./HelpTour"));

// Панель онлайн-справки по кнопке «?» (эпик #255, H2). Порт HelpDrawer из смежного
// проекта SNS CRM, адаптированный под ANYskin: контекст экрана берётся из пропа `view`
// (react-router в ANYskin нет — экран определяется стейтом view в App.tsx), а не из
// useLocation(). Скриншоты и туры — задачи H8/H6. Полноэкранный центр /help — H5.

const norm = (s: string) => s.toLowerCase().replace(/ё/g, "е");

export default function HelpDrawer({ view, onOpenCenter }: { view: string; onOpenCenter?: () => void }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState<HelpArticle[] | null>(null);
  const [pages, setPages] = useState<Record<string, string>>({});
  const [tours, setTours] = useState<Tour[]>([]);
  const [imgSlots, setImgSlots] = useState<Set<string>>(new Set());
  const [activeTour, setActiveTour] = useState<TourStep[] | null>(null);
  const [current, setCurrent] = useState<number | null>(null);
  const [wantSlug, setWantSlug] = useState<string | null>(null);   // статья, запрошенная инлайн-подсказкой
  const [atIndex, setAtIndex] = useState(false);   // пользователь сам вернулся в оглавление
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  // Туры тянем сразу (лёгкий запрос) — нужны для бейджа «пройдите тур» на кнопке «?».
  useEffect(() => {
    apiFetch<{ rows: Tour[] }>("/api/help/tours").then((t) => setTours(t.rows)).catch(() => {});
  }, []);

  // Статьи и картинки — лениво, при первом открытии панели.
  useEffect(() => {
    if (!open || rows) return;
    Promise.all([
      apiFetch<{ pages: Record<string, string>; rows: HelpArticle[] }>("/api/help"),
      apiFetch<{ rows: { slot: string }[] }>("/api/help/images").catch(() => ({ rows: [] as { slot: string }[] })),
    ])
      .then(([a, im]) => { setPages(a.pages); setRows(a.rows); setImgSlots(new Set(im.rows.map((r) => r.slot))); })
      .catch((e) => setErr(e.message || "Не удалось загрузить справку"));
  }, [open, rows]);

  // Запуск тура гасит бейдж «пройдите тур» (H10, #265): отмечаем пройденным на бэке и локально.
  const markSeen = (id: number) => {
    setTours((ts) => ts.map((x) => (x.id === id ? { ...x, seen: true } : x)));
    apiFetch(`/api/help/tours/${id}/seen`, { method: "POST" }).catch(() => {});
  };
  const startTour = (t: Tour) => { setOpen(false); setActiveTour(t.steps); markSeen(t.id); };

  // Запуск тура из «Справочного центра»: тот переводит экран и шлёт глобальное событие,
  // а раннер TourOverlay живёт здесь (панель смонтирована на всех экранах).
  useEffect(() => {
    const h = (e: Event) => {
      const t = (e as CustomEvent<Tour>).detail;
      if (t?.steps?.length) {
        setOpen(false); setActiveTour(t.steps);
        setTours((ts) => ts.map((x) => (x.id === t.id ? { ...x, seen: true } : x)));
        apiFetch(`/api/help/tours/${t.id}/seen`, { method: "POST" }).catch(() => {});
      }
    };
    window.addEventListener("anyskin:start-tour", h as EventListener);
    return () => window.removeEventListener("anyskin:start-tour", h as EventListener);
  }, []);

  // Инлайн-подсказка «?» у блока экрана (B4, #292): открыть панель на конкретной статье.
  // Статьи могут быть ещё не загружены — запоминаем slug и выбираем статью, когда придут.
  useEffect(() => {
    const h = (e: Event) => {
      const slug = (e as CustomEvent<{ slug?: string }>).detail?.slug;
      if (slug) { setWantSlug(slug); setQ(""); }
      setOpen(true);                              // без slug — справка по текущему экрану (кнопка в мобильной шапке)
    };
    window.addEventListener("anyskin:open-help", h as EventListener);
    return () => window.removeEventListener("anyskin:open-help", h as EventListener);
  }, []);

  useEffect(() => {
    if (!wantSlug || !rows) return;
    const a = rows.find((r) => r.slug === wantSlug);
    if (a) setCurrent(a.id);
    setWantSlug(null);
  }, [wantSlug, rows]);

  // Esc закрывает панель.
  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", h);
    return () => document.removeEventListener("keydown", h);
  }, [open]);

  const key = pageKeyFor(view);
  const forPage = useMemo(() => (rows ?? []).filter((a) => a.page_key === key), [rows, key]);
  const pageTours = useMemo(() => tours.filter((t) => t.page_key === key || t.page_key === null), [tours, key]);
  // При смене экрана сбрасываем выбранную статью — покажем статью нового экрана.
  useEffect(() => { if (!wantSlug) { setCurrent(null); setAtIndex(false); } }, [view, wantSlug]);
  // Закрыли панель — следующее открытие снова показывает статью экрана.
  useEffect(() => { if (!open) setAtIndex(false); }, [open]);
  useEffect(() => {
    if (open && !atIndex && current === null && forPage.length) setCurrent(forPage[0].id);
  }, [open, forPage, current, atIndex]);

  const nq = norm(q.trim());
  const hits = nq ? (rows ?? []).filter((a) => norm(a.title).includes(nq) || norm(a.body).includes(nq)) : (rows ?? []);
  const article = (rows ?? []).find((a) => a.id === current) ?? null;

  const tourBtn = (t: Tour) => (
    <button key={t.id} onClick={() => startTour(t)}
      className="w-full text-left rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 hover:bg-indigo-100 cursor-pointer">
      <div className="text-sm font-medium text-indigo-800 flex items-center gap-2">
        <span>▶ {t.title}</span>
        {!t.seen && <span className="rounded-full bg-indigo-600 text-white text-[10px] font-bold px-1.5 py-0.5">пройдите тур</span>}
      </div>
      {t.intro && <div className="text-xs text-slate-600">{t.intro}</div>}
      <div className="text-[11px] text-slate-400">{t.steps.length} шагов · подсветит элементы на экране</div>
    </button>
  );

  return (
    <>
      {activeTour && <Suspense fallback={null}><TourOverlay steps={activeTour} onClose={() => setActiveTour(null)} /></Suspense>}
      <button onClick={() => setOpen(true)} data-help="help-button"
        title="Справка по этому экрану" aria-label="Открыть справку"
        className="fixed right-3 top-3 z-(--z-fab) w-10 h-10 hidden md:flex rounded-full border border-slate-300 bg-white/90 backdrop-blur text-slate-600 shadow-sm hover:bg-slate-100 hover:text-indigo-700 items-center justify-center cursor-pointer">
        <HelpCircle className="w-5 h-5" />
        {/* Бейдж-точка: на этом экране есть непройденный тур (H10, #265). */}
        {pageTours.some((t) => !t.seen) && (
          <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-indigo-600 ring-2 ring-white" />
        )}
      </button>

      {open && <HelpLayer onClose={() => setOpen(false)} />}
      {open && (
        <div className="fixed inset-0 z-(--z-sheet) flex justify-end" role="dialog" aria-modal="true" aria-label="Справка">
          <div className="flex-1 bg-slate-900/20" onClick={() => setOpen(false)} />
          <aside className="w-full sm:w-[30rem] max-w-full bg-white border-l border-slate-200 shadow-xl flex flex-col pt-[env(safe-area-inset-top)]">
            <header className="px-4 py-3 border-b border-slate-100 flex items-center gap-2">
              <h2 className="font-semibold text-slate-900">Справка</h2>
              {pages[key] && <span className="text-xs text-slate-400 truncate">{pages[key]}</span>}
              <button onClick={() => setOpen(false)} aria-label="Закрыть"
                className="ml-auto -mr-2 grid place-items-center w-11 h-11 rounded-xl text-slate-400 hover:bg-slate-100 hover:text-slate-900 text-2xl leading-none cursor-pointer">×</button>
            </header>

            <div className="px-4 py-2 border-b border-slate-100">
              <input value={q} onChange={(e) => { setQ(e.target.value); setCurrent(null); setAtIndex(true); }}
                placeholder="поиск по справке…"
                className="w-full rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm focus:border-slate-400 focus:outline-none" />
            </div>

            <div className="flex-1 overflow-auto">
              {err && <p className="p-4 text-sm text-rose-600">{err}</p>}
              {!rows && !err && <p className="p-4 text-sm text-slate-400">Загружаем…</p>}

              {rows && article && !nq && (
                <div className="p-4">
                  <button onClick={() => { setCurrent(null); setAtIndex(true); }} className="text-xs text-indigo-600 hover:underline cursor-pointer">← все разделы</button>
                  <h3 className="mt-2 text-base font-semibold text-slate-900">{article.title}</h3>
                  <Markup text={article.body} images={imgSlots} />
                  {pageTours.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-slate-100 space-y-2">
                      <div className="text-xs uppercase tracking-wide text-slate-400">Показать на экране</div>
                      {pageTours.map(tourBtn)}
                    </div>
                  )}
                  {forPage.length > 1 && (
                    <div className="mt-4 pt-3 border-t border-slate-100">
                      <div className="text-xs text-slate-500 mb-1">Ещё об этом экране</div>
                      {forPage.filter((a) => a.id !== article.id).map((a) => (
                        <button key={a.id} onClick={() => { setCurrent(a.id); setAtIndex(false); }}
                          className="block text-sm text-indigo-600 hover:underline cursor-pointer">{a.title}</button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {rows && (!article || nq) && (
                <div className="p-4 space-y-4">
                  {!nq && pageTours.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Пошаговые туры</div>
                      <div className="space-y-2">{pageTours.map(tourBtn)}</div>
                    </div>
                  )}
                  {!nq && forPage.length > 0 && (
                    <div>
                      <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">Этот экран</div>
                      <ul className="space-y-1">
                        {forPage.map((a) => (
                          <li key={a.id}>
                            <button onClick={() => { setCurrent(a.id); setAtIndex(false); }}
                              className="text-sm text-left text-indigo-700 font-medium hover:underline cursor-pointer">{a.title}</button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-400 mb-1">
                      {nq ? `Найдено: ${hits.length}` : "Все разделы"}
                    </div>
                    <ul className="space-y-1">
                      {hits.map((a) => (
                        <li key={a.id} className="flex items-baseline gap-2">
                          <button onClick={() => { setQ(""); setCurrent(a.id); setAtIndex(false); }}
                            className="text-sm text-left text-slate-700 hover:text-indigo-700 hover:underline cursor-pointer">{a.title}</button>
                          {a.page_key && <span className="text-[11px] text-slate-400">{pages[a.page_key] ?? a.page_key}</span>}
                        </li>
                      ))}
                      {!hits.length && <li className="text-sm text-slate-400">Ничего не найдено</li>}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            <footer className="px-4 py-2 border-t border-slate-100 flex items-center gap-2 text-xs text-slate-400">
              {onOpenCenter && (
                <button onClick={() => { setOpen(false); onOpenCenter(); }}
                  className="text-indigo-600 font-medium hover:underline cursor-pointer">Справочный центр →</button>
              )}
              <span className="ml-auto">Тексты правит администратор.</span>
            </footer>
          </aside>
        </div>
      )}
    </>
  );
}


/** Справка открыта: фон не прокручивается, «Назад» телефона закрывает её (мобильный аудит, M-40). */
function HelpLayer({ onClose }: { onClose: () => void }) {
  const ref = useRef(onClose);
  ref.current = onClose;
  useEffect(() => pushLayer(() => ref.current()), []);
  return null;
}
